import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Plus, X } from "lucide-react";
import PageLayout from "@/components/layout/PageLayout";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { usePortfolioData } from "@/hooks/use-portfolio-data";
import {
  assignMaintenanceContractor,
  createExpense,
  createMaintenanceRequest,
  updateMaintenanceRequest,
  updateMaintenanceStatus,
} from "@/services/dataService";
import { houseName } from "@/lib/ledger";

/** Open jobs grouped by stage; one tap moves a job to the next stage. */
const STAGES = [
  { key: "new", label: "New", next: "scheduled", action: "Schedule" },
  { key: "acknowledged", label: "New", next: "scheduled", action: "Schedule" },
  { key: "scheduled", label: "Scheduled", next: "inProgress", action: "Start" },
  { key: "inProgress", label: "In progress", next: "completed", action: "Mark done" },
  { key: "awaitingParts", label: "Waiting for parts", next: "inProgress", action: "Resume" },
];
const GROUPS = [
  { label: "New", keys: ["new", "acknowledged"] },
  { label: "Scheduled", keys: ["scheduled"] },
  { label: "In progress", keys: ["inProgress"] },
  { label: "Waiting for parts", keys: ["awaitingParts"] },
];
const PRIORITIES = [
  { value: "normal", label: "Normal" },
  { value: "high", label: "High" },
  { value: "urgent", label: "Urgent" },
];
const DAY = 86400000;
const inputClass = "h-9 w-full rounded-lg border border-input bg-background px-3 text-sm";

const age = (ts) => {
  const days = Math.max(0, Math.floor((Date.now() - (ts || Date.now())) / DAY));
  return days === 0 ? "today" : days === 1 ? "1 day" : `${days} days`;
};

export default function MaintenanceRequests() {
  const { toast } = useToast();
  const { data, isLoading, error, refresh } = usePortfolioData(["properties", "maintenance", "contractors"]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ propertyId: "", title: "", priority: "normal", contractorId: "" });
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [finishing, setFinishing] = useState(null); // job being completed → cost prompt
  const [cost, setCost] = useState("");

  const propertiesById = useMemo(() => new Map(data.properties.map((p) => [String(p._id), p])), [data.properties]);
  const contractorsById = useMemo(() => new Map(data.contractors.map((c) => [String(c._id), c])), [data.contractors]);
  const contractorName = (id) => {
    const c = id && contractorsById.get(String(id));
    return c ? c.contactName && c.contactName !== c.companyName ? `${c.contactName} (${c.companyName})` : c.companyName : "";
  };

  const open = data.maintenance.filter((job) => !["completed", "cancelled"].includes(job.status));
  const done = data.maintenance
    .filter((job) => job.status === "completed" && (job.completedDate || job.updatedAt || 0) > Date.now() - 60 * DAY)
    .sort((a, b) => (b.completedDate || 0) - (a.completedDate || 0));

  const fail = (e) => toast({ title: "Couldn't save", description: e.message, variant: "destructive" });

  async function createJob(event) {
    event.preventDefault();
    if (!form.propertyId || !form.title.trim()) {
      toast({ title: "Pick a house and say what's wrong", variant: "destructive" });
      return;
    }
    setSaving(true);
    try {
      const contractor = form.contractorId ? contractorsById.get(form.contractorId) : null;
      const job = await createMaintenanceRequest({
        propertyId: form.propertyId,
        title: form.title.trim(),
        descriptionText: form.title.trim(),
        category: contractor?.specialty?.[0] || "General",
        priority: form.priority,
      });
      if (form.contractorId) {
        await assignMaintenanceContractor({ requestId: job._id || job.id, contractorId: form.contractorId });
      }
      setForm((prev) => ({ ...prev, title: "", contractorId: "" }));
      setShowForm(false);
      refresh();
    } catch (e) {
      fail(e);
    } finally {
      setSaving(false);
    }
  }

  async function advance(job) {
    const stage = STAGES.find((s) => s.key === job.status) || STAGES[0];
    if (stage.next === "completed") {
      setCost(job.estimatedCost ? String(job.estimatedCost) : "");
      setFinishing(job);
      return;
    }
    setBusyId(job._id);
    try {
      await updateMaintenanceStatus({ id: job._id, status: stage.next });
      refresh();
    } catch (e) {
      fail(e);
    } finally {
      setBusyId(null);
    }
  }

  async function finish(addToLedger) {
    const job = finishing;
    const amount = Number.parseFloat(String(cost).replace(/[$,\s]/g, ""));
    setBusyId(job._id);
    try {
      await updateMaintenanceStatus({ id: job._id, status: "completed" });
      if (addToLedger && amount > 0) {
        await updateMaintenanceRequest({ id: job._id, actualCost: amount });
        await createExpense({
          propertyId: job.propertyId,
          title: `Repair: ${job.title}`,
          amount,
          category: "maintenance",
          date: Date.now(),
          isRecurring: false,
          notes: [contractorName(job.contractorId), `maintenance ${job._id}`].filter(Boolean).join(" · "),
        });
        toast({ title: "Done — cost added to the ledger" });
      } else {
        toast({ title: "Marked done" });
      }
      setFinishing(null);
      refresh();
    } catch (e) {
      fail(e);
    } finally {
      setBusyId(null);
    }
  }

  async function cancelJob(job) {
    if (!window.confirm(`Cancel “${job.title}”?`)) return;
    try {
      await updateMaintenanceStatus({ id: job._id, status: "cancelled" });
      refresh();
    } catch (e) {
      fail(e);
    }
  }

  const row = (job, finished = false) => {
    const stage = STAGES.find((s) => s.key === job.status);
    const property = propertiesById.get(String(job.propertyId));
    return (
      <tr key={job._id} className="group">
        <td className="max-w-[260px]">
          <span className="block truncate font-medium">{job.title}</span>
          {["urgent", "emergency", "high"].includes(job.priority) && !finished ? (
            <span className={`text-xs ${job.priority === "high" ? "text-amber-700 dark:text-amber-300" : "text-rose-700 dark:text-rose-300"}`}>
              {job.priority === "high" ? "High priority" : "Urgent"}
            </span>
          ) : null}
        </td>
        <td className="max-w-[180px] truncate">
          {property ? (
            <Link to={`/houses/${job.propertyId}`} className="hover:underline">
              {houseName(property)}
            </Link>
          ) : (
            "—"
          )}
        </td>
        <td className="max-w-[200px] truncate text-muted-foreground">{contractorName(job.contractorId) || "—"}</td>
        <td className="whitespace-nowrap text-muted-foreground">
          {finished
            ? new Date(job.completedDate || job.updatedAt).toLocaleDateString("en-US", { month: "short", day: "numeric" })
            : age(job.createdAt)}
        </td>
        <td className="num">
          {job.actualCost || job.estimatedCost
            ? `$${Number(job.actualCost || job.estimatedCost).toLocaleString()}`
            : <span className="text-muted-foreground">—</span>}
        </td>
        <td className="whitespace-nowrap text-right">
          {!finished && stage ? (
            <>
              <Button size="sm" variant={stage.next === "completed" ? "default" : "outline"} className="h-8" disabled={busyId === job._id} onClick={() => advance(job)}>
                {busyId === job._id ? <Loader2 className="h-4 w-4 animate-spin" /> : stage.action}
              </Button>
              <button
                type="button"
                onClick={() => cancelJob(job)}
                className="ml-1 inline-grid h-8 w-8 place-items-center rounded-md text-muted-foreground opacity-0 hover:bg-muted focus:opacity-100 group-hover:opacity-100"
                aria-label={`Cancel ${job.title}`}
                title="Cancel job"
              >
                <X className="h-4 w-4" />
              </button>
            </>
          ) : null}
        </td>
      </tr>
    );
  };

  return (
    <PageLayout
      title="Maintenance"
      actions={
        <Button size="sm" className="gap-1.5" onClick={() => setShowForm((v) => !v)}>
          <Plus className="h-4 w-4" /> New job
        </Button>
      }
    >
      {error ? <p className="mb-3 text-sm text-destructive">{error.message}</p> : null}

      {showForm ? (
        <form onSubmit={createJob} className="hh-panel mb-5 grid gap-3 p-4 sm:grid-cols-[1fr_2fr_140px_1fr_auto] sm:items-end">
          <label className="text-sm">
            <span className="mb-1 block text-muted-foreground">House</span>
            <select className={inputClass} value={form.propertyId} onChange={(e) => setForm({ ...form, propertyId: e.target.value })}>
              <option value="">Choose…</option>
              {data.properties.map((p) => (
                <option key={p._id} value={String(p._id)}>
                  {houseName(p)}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm">
            <span className="mb-1 block text-muted-foreground">What's wrong</span>
            <input className={inputClass} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Kitchen sink leaking" autoFocus />
          </label>
          <label className="text-sm">
            <span className="mb-1 block text-muted-foreground">Priority</span>
            <select className={inputClass} value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>
              {PRIORITIES.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm">
            <span className="mb-1 block text-muted-foreground">Contractor (optional)</span>
            <select className={inputClass} value={form.contractorId} onChange={(e) => setForm({ ...form, contractorId: e.target.value })}>
              <option value="">—</option>
              {data.contractors.map((c) => (
                <option key={c._id} value={String(c._id)}>
                  {contractorName(c._id)}
                </option>
              ))}
            </select>
          </label>
          <Button type="submit" disabled={saving} className="h-9">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add job"}
          </Button>
        </form>
      ) : null}

      <div className="hh-panel overflow-x-auto">
        <table className="hh-table min-w-[760px]">
          <thead>
            <tr>
              <th>Job</th>
              <th>House</th>
              <th>Contractor</th>
              <th>Open for</th>
              <th className="num">Cost</th>
              <th>
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          {GROUPS.map((group) => {
            const jobs = open.filter((job) => group.keys.includes(job.status)).sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
            if (!jobs.length) return null;
            return (
              <tbody key={group.label}>
                <tr>
                  <td colSpan={6} className="bg-muted/40 py-1.5 text-xs font-medium text-muted-foreground">
                    {group.label} · {jobs.length}
                  </td>
                </tr>
                {jobs.map((job) => row(job))}
              </tbody>
            );
          })}
          {!open.length ? (
            <tbody>
              <tr>
                <td colSpan={6} className="py-10 text-center text-sm text-muted-foreground">
                  {isLoading ? "Loading…" : "No open jobs. Nice."}
                </td>
              </tr>
            </tbody>
          ) : null}
          {done.length ? (
            <tbody>
              <tr>
                <td colSpan={6} className="bg-muted/40 py-1.5 text-xs font-medium text-muted-foreground">
                  Done in the last 60 days · {done.length}
                </td>
              </tr>
              {done.map((job) => row(job, true))}
            </tbody>
          ) : null}
        </table>
      </div>

      <Dialog open={Boolean(finishing)} onOpenChange={(value) => !value && setFinishing(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mark “{finishing?.title}” done</DialogTitle>
            <DialogDescription>Add what it cost to the ledger for this house?</DialogDescription>
          </DialogHeader>
          <label className="text-sm">
            <span className="mb-1 block text-muted-foreground">Cost</span>
            <input
              className={inputClass}
              inputMode="decimal"
              value={cost}
              onChange={(e) => setCost(e.target.value)}
              placeholder="0.00"
              autoFocus
              onKeyDown={(e) => e.key === "Enter" && finish(true)}
            />
          </label>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => finish(false)} disabled={Boolean(busyId)}>
              Just mark done
            </Button>
            <Button onClick={() => finish(true)} disabled={Boolean(busyId) || !(Number.parseFloat(cost) > 0)}>
              Done + add to ledger
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageLayout>
  );
}
